import type { InvoiceBase } from '@portal/types/nexudus';
import type { DiscountLine } from '../BookingSummary';
import { getInvoiceDiscounts, NO_TEAM_DISCOUNTS } from './getInvoiceDiscounts';
import type { TeamDiscounts } from './getInvoiceDiscounts';

export interface InvoiceSummary {
  subtotal: number;
  tax: number;
  /** Gross total, VAT-inclusive, after booking credits but before account credits. */
  total: number;
  totalDiscount: number;
  discountLines: DiscountLine[];
  /** Account credit applied to the gross total (0 when none applies). */
  accountCredits: number;
  /** What the customer actually pays: total − accountCredits. */
  amountDue: number;
}

export interface InvoiceSummaryOptions {
  /**
   * The user's available account-credit balance (`CurrentBalance` from
   * `/allowances/plans`). At the basket stage Nexudus has not raised a real
   * invoice yet, so `PreviewInvoice` never reports account credit — we predict
   * how much it will absorb as `min(balance, gross total)`. The payment page
   * shows the authoritative figure from the real invoice.
   */
  accountBalance?: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Derive display totals from an invoice preview.
 * Only call this when you have an invoice — manage loading state at the call site.
 *
 * When teamDiscounts is provided, subtotal becomes the pre-discount amount
 * and discountLines break down the applied discounts.
 *
 * Calculation order (BCD-1738, mirrors the payment breakdown decisions):
 * discount code → team discount → booking credits → VAT → gross total →
 * account credits → amount due. Everything up to and including the gross total
 * comes from Nexudus (booking credits reduce the line price, so `TaxAmount` is
 * already VAT on the net after booking credits); only the account credit is
 * derived here.
 */
export function getInvoiceSummary(
  invoice: InvoiceBase,
  teamDiscounts: TeamDiscounts = NO_TEAM_DISCOUNTS,
  { accountBalance = 0 }: InvoiceSummaryOptions = {},
): InvoiceSummary {
  const tax = invoice.TaxAmount ?? 0;
  const postDiscountSubtotal = (invoice.TotalAmount ?? 0) - tax;
  // Gross, VAT-inclusive, before any account credit. `DueAmount` is net of
  // account credits on a real invoice, so it can't stand in for the total.
  const total = invoice.TotalAmount ?? postDiscountSubtotal + tax;

  const discountLines = getInvoiceDiscounts(invoice, teamDiscounts);
  const totalDiscount = discountLines
    .filter((l) => l.type !== 'credit')
    .reduce((sum, l) => sum + l.amount, 0);
  const totalCredits = discountLines
    .filter((l) => l.type === 'credit')
    .reduce((sum, l) => sum + l.amount, 0);
  const subtotal = postDiscountSubtotal + totalDiscount + totalCredits;

  // A real invoice reports the credit it applied via `TotalAmount − DueAmount`
  // (the preview never does — `DueAmount` mirrors `TotalAmount` there, giving 0).
  // Trust that when present; otherwise predict from the balance.
  const invoiceCredits =
    invoice.DueAmount != null ? Math.max(0, total - invoice.DueAmount) : 0;
  const predictedCredits = Math.min(Math.max(0, accountBalance), total);
  // Nothing to settle when the gross total is £0 — no credit is drawn down.
  const accountCredits =
    total < 0.005
      ? 0
      : round2(invoiceCredits > 0.005 ? invoiceCredits : predictedCredits);
  const amountDue = Math.max(0, round2(total - accountCredits));

  return {
    subtotal,
    tax,
    total,
    totalDiscount,
    discountLines,
    accountCredits,
    amountDue,
  };
}
